import CustomAlert from '@common/components/Feedback/CustomAlert';
import { v4 as uuidv4 } from 'uuid';
import { FilterKeys } from '@common/enums/query-params/filters';
import { AddIcon, CopyIcon, EditIcon, SaveIcon } from '@common/utils/icons/icons';
import { SelectChangeEvent } from '@mui/material';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SelectInput from 'components/forms/SelectInput';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  selectFleetDataSetsData,
  selectFleetDataSetsRestState,
} from 'store/fleet-management/fleet-management-slice-selectors';
import GeneralDatasetConfigs from './GeneralDatasetConfigs';
import DatasetFields from './DatasetFields';
import DatasetFilters from './DatasetFilters';
import FleetDataSet, { emptyDataset } from 'models/fleet-management/FleetDataSet';
import {
  createNewFleetDataSet,
  saveFleetDataSet,
} from 'store/fleet-management/fleet-management-actions';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { patchDataSetsRestState } from 'store/fleet-management/fleet-management-slice';
import DatasetWidgetsInfo from './DatasetWidgetsInfo';
import DataPreview from './DataPreview';

const DatasetBlock: React.FC = () => {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useSearchParams();
  const queryDatasetId = queryParams.get(FilterKeys.FleetDataset);

  const dispatch = useAppDispatch();

  const datasets = useAppSelector((state) => selectFleetDataSetsData(state));
  const datasetsRestState = useAppSelector((state) => selectFleetDataSetsRestState(state));

  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [editingDataset, setEditingDataset] = useState<{
    dataset: FleetDataSet;
    duplicate: boolean;
  } | null>(null);

  useEffect(() => {
    if (queryDatasetId != null) {
      setSelectedDatasetId(queryDatasetId);
      setEditingDataset(null);
    }
  }, [queryDatasetId]);

  const changeDatasetHandler = (event: SelectChangeEvent<unknown>) => {
    setQueryParams((prevState) => {
      prevState.set(FilterKeys.FleetDataset, event.target.value as string);
      return prevState;
    });
  };

  const editClickHandler = () => {
    const ds = datasets?.find((ds) => ds.id === selectedDatasetId);
    if (ds == null) return;
    setEditingDataset({ dataset: ds, duplicate: false });
  };

  const duplicateClickHandler = () => {
    const ds = datasets?.find((ds) => ds.id === selectedDatasetId);
    if (ds == null) return;
    setEditingDataset({
      dataset: { ...ds, filters: ds.filters.map((f) => ({ ...f, id: uuidv4() })) },
      duplicate: true,
    });
  };

  const createDatasetClickHandler = () => {
    setEditingDataset({
      dataset: emptyDataset,
      duplicate: true,
    });
  };

  const saveClickHandler = () => {
    setEditingDataset(null);
    if (editingDataset == null) return;

    if (editingDataset.duplicate) {
      dispatch(
        createNewFleetDataSet({
          ...editingDataset.dataset,
          id: undefined,
          isDefault: false,
        }),
      );
    } else {
      dispatch(saveFleetDataSet(editingDataset.dataset));
    }
  };

  const editDataSetHandler = (ds: Partial<FleetDataSet>) => {
    setEditingDataset((prevState) => {
      if (prevState == null) return prevState;
      return { ...prevState, dataset: { ...prevState.dataset, ...ds } };
    });
  };

  const resetDatasetsRestStateHandler = () => {
    dispatch(
      patchDataSetsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  if (datasets == null) return null;

  const selectedDataset =
    editingDataset?.dataset ?? datasets?.find((ds) => ds.id === selectedDatasetId);

  return (
    <React.Fragment>
      <Stack height="100%" flex={1} overflow="hidden" spacing={2}>
        <Stack direction="row" alignItems="end" justifyContent="space-between" py={1}>
          <Stack>
            <Typography variant="h4">{t('title-datasets-and-filters')}</Typography>
            <Typography variant="body1">{t('info-manage-datasets')}</Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            {editingDataset == null && (
              <Button
                startIcon={<CopyIcon />}
                onClick={duplicateClickHandler}
                disabled={selectedDataset == null}
              >
                {t('button-duplicate')}
              </Button>
            )}
            {editingDataset != null ? (
              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveClickHandler}>
                {t('button-save')}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={editClickHandler}
                disabled={selectedDataset == null}
              >
                {t('button-edit')}
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={4}
          sx={{ height: '100%', width: '100%', overflow: 'hidden' }}
        >
          <Stack spacing={4} sx={(theme) => ({ maxWidth: theme.spacing(56), width: '100%' })}>
            <Stack spacing={2}>
              <Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h2">{t('title-dataset-selector')}</Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={createDatasetClickHandler}
                    disabled={editingDataset != null}
                  >
                    {t('button-add')}
                  </Button>
                </Stack>
                <SelectInput
                  size="small"
                  disabled={editingDataset != null}
                  value={selectedDatasetId ?? ''}
                  onChange={changeDatasetHandler}
                  sx={(theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    color: '#FFF',
                    borderRadius: 2,
                    overflow: 'hidden',
                    '& .MuiSelect-icon': { fill: '#FFF' },
                  })}
                >
                  <Typography variant="overline" px={2}>
                    {t('title-generic-datasets')}
                  </Typography>
                  {datasets
                    .filter((ds) => ds.isDefault)
                    .map((ds) => (
                      <MenuItem key={ds.id} value={ds.id} sx={{ pl: 2 }}>
                        <ListItemText sx={{ pl: 2 }}>{ds.name}</ListItemText>
                      </MenuItem>
                    ))}
                  <Typography variant="overline" px={2}>
                    {t('title-personal-datasets')}
                  </Typography>
                  {datasets
                    .filter((ds) => !ds.isDefault)
                    .map((ds) => (
                      <MenuItem key={ds.id} value={ds.id} sx={{ pl: 2 }}>
                        <ListItemText sx={{ pl: 2 }}>{ds.name}</ListItemText>
                      </MenuItem>
                    ))}
                </SelectInput>
              </Stack>
              {selectedDataset == null ? (
                <CustomAlert severity="info" header={t('info-select-dataset-to-configure')} />
              ) : (
                <React.Fragment>
                  <GeneralDatasetConfigs
                    dataset={selectedDataset}
                    isEditing={editingDataset != null}
                    onEdit={editDataSetHandler}
                  />
                </React.Fragment>
              )}
            </Stack>
            {selectedDataset != null && (
              <DatasetFields
                dataset={selectedDataset}
                isEditing={editingDataset != null}
                onEdit={editDataSetHandler}
              />
            )}
          </Stack>

          <Stack
            sx={(theme) => ({
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              maxWidth: theme.spacing(56),
            })}
            flex={1}
          >
            {selectedDataset != null && (
              <DatasetFilters
                dataset={selectedDataset}
                isEditing={editingDataset != null}
                onEdit={editDataSetHandler}
              />
            )}
          </Stack>
          <Stack sx={{ width: '100%', height: '100%', overflow: 'hidden' }} flex={1} spacing={4}>
            <DatasetWidgetsInfo isEditing={editingDataset != null} dataset={selectedDataset} />
            <DataPreview dataset={selectedDataset} />
          </Stack>
        </Stack>
      </Stack>
      <CustomSnackbar
        open={datasetsRestState.patchStatus === HttpFetchStatus.Success}
        title={t('success-saving-dataset')}
        onClose={resetDatasetsRestStateHandler}
      />
      <CustomSnackbar
        open={datasetsRestState.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-dataset')}
        onClose={resetDatasetsRestStateHandler}
      />
      <CustomSnackbar
        open={datasetsRestState.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-dataset')}
        onClose={resetDatasetsRestStateHandler}
      />
    </React.Fragment>
  );
};

export default DatasetBlock;
